/**
 * ATL-001415 — small shared pieces for the Email Intake pages.
 */
import i18next, { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, PauseCircle, ShieldAlert } from 'lucide-react';
import { Badge } from '../../core/components/ui/Badge';
import {
  apiErrorCode,
  apiErrorMessage,
  type EmailMailbox,
  type InboundEmailSummary,
} from '../../hooks/use-email-intake';

export const QUEUE_PATH = '/dispatch/email-intake';
/** Email Intake settings live under Settings → Integrations. */
export const INTEGRATIONS_PATH = '/admin/integrations';
export const SETTINGS_PATH = `${INTEGRATIONS_PATH}/email-intake`;

/**
 * Per module: the company preference that turns its quote workflow on
 * (Settings → Company Preferences → Enable Quote Workflow) and its Quotes page.
 */
export const QUOTE_WORKFLOW: Record<
  string,
  {
    pref:
      | 'enableQuotes'
      | 'enableQuotesUspsCarrier'
      | 'enableQuotesIntermodal'
      | 'enableQuotesFtlbrokerage'
      | 'enableQuotesLivestock';
    path: string;
  }
> = {
  mod_dispatch_freight_main: { pref: 'enableQuotes', path: '/quotes' },
  mod_usps_main: { pref: 'enableQuotesUspsCarrier', path: '/usps/quotes' },
  mod_intermodal_main: { pref: 'enableQuotesIntermodal', path: '/intermodal/quotes' },
  mod_ftl_brokerage_main: { pref: 'enableQuotesFtlbrokerage', path: '/freight/quotes' },
  mod_livestock_main: { pref: 'enableQuotesLivestock', path: '/livestock/quotes' },
};

/** Where a created quote opens: the dispatch deep link finds it in its module's grid. */
export function quoteHref(id: string, module: string | null | undefined): string {
  if (!module || module === 'mod_dispatch_freight_main') return `/quotes/${id}`;
  return QUOTE_WORKFLOW[module]?.path ?? `/quotes/${id}`;
}

/**
 * The settings' old address (/settings/email-intake) — bookmarks, notification
 * links and OAuth returns already sent keep working, query string included.
 */
export function LegacySettingsRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: SETTINGS_PATH, search, hash }} replace />;
}

/**
 * A reviewer-facing name for an extraction field path: `payAmount` →
 * "Customer rate", `stops.0.date` / `stops[0].date` → "Stop 1 date".
 * Unknown paths fall back to the path itself.
 */
export function fieldLabel(t: TFunction, path: string | null | undefined): string {
  if (!path) return '';
  const norm = path.replace(/\[(\d+)\]/g, '.$1');
  const stop = norm.match(/^stops\.(\d+)(?:\.(\w+))?/);
  if (stop) {
    const n = Number(stop[1]) + 1;
    return stop[2]
      ? t('email-intake.fields.stopField', {
          n,
          field: t(`email-intake.fields.stop.${stop[2]}`, { defaultValue: stop[2] }),
        })
      : t('email-intake.fields.stopN', { n });
  }
  const [root, sub] = norm.split('.');
  const parent = t(`email-intake.fields.${root}`, { defaultValue: root });
  // carrier.contactName → "Carrier contact name"
  if (sub && hasTranslation(`email-intake.fields.sub.${sub}`)) {
    return t('email-intake.fields.nested', {
      parent,
      field: t(`email-intake.fields.sub.${sub}`),
    });
  }
  return t(`email-intake.fields.${root}`, { defaultValue: path });
}

/**
 * Why an email failed or was ignored, as stored ("CODE" or "code: detail"),
 * in the reader's language — the same text on the detail page and in
 * Monitoring. Unknown codes show as stored.
 */
export function failureReasonText(t: TFunction, raw: string | null | undefined): string {
  if (!raw) return '';
  const code = raw.split(':')[0].trim();
  for (const key of [`email-intake.failureReason.${code}`, `email-intake.errors.${code}`]) {
    if (hasTranslation(key)) return t(key);
  }
  return raw;
}

/** Translated name of the module a mailbox creates loads in. */
export function moduleLabel(t: TFunction, variant: string): string {
  return t(`email-intake.modules.${variant}`, { defaultValue: variant });
}

/** Where "Back" from an email returns: the queue with the filters it was opened from. */
export function useQueueBackHref(): string {
  const state = useLocation().state as { queueSearch?: unknown } | null;
  const search = typeof state?.queueSearch === 'string' ? state.queueSearch : '';
  return search.startsWith('?') ? `${QUEUE_PATH}${search}` : QUEUE_PATH;
}

/** AI confidence as a coloured pill: high ≥ 90 %, medium ≥ 70 %, else low. */
export function ConfidencePill({ value }: { value: number | null | undefined }) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const pct = Math.round(Number(value) * 100);
  const variant = pct >= 90 ? 'success' : pct >= 70 ? 'warning' : 'error';
  return (
    <Badge variant={variant} data-testid="email-confidence">
      {pct}%
    </Badge>
  );
}

/**
 * Mailboxes that need someone's attention — signed out, failing or paused —
 * as one alert with a way to fix it (settings for admins, a hint otherwise).
 */
export function MailboxHealthAlert({
  mailboxes,
  canConfigure,
}: {
  mailboxes: EmailMailbox[];
  canConfigure: boolean;
}) {
  const { t } = useTranslation();
  const reauth = mailboxes.filter((m) => m.status === 'needs_reauth');
  const failing = mailboxes.filter((m) => m.status === 'error');
  const paused = mailboxes.filter((m) => m.status === 'paused');
  const problem = reauth.length > 0 ? 'reauth' : failing.length > 0 ? 'error' : null;
  const allPaused = !problem && mailboxes.length > 0 && paused.length === mailboxes.length;
  if (!problem && !allPaused) return null;

  const affected = problem === 'reauth' ? reauth : problem === 'error' ? failing : paused;
  const tone = problem
    ? 'border-[#f5c2c2] bg-[#fdf3f3] text-[#a54242]'
    : 'border-[#d6e4f5] bg-[#f3f8fd] text-[#2c5d93]';
  const Icon = problem === 'reauth' ? ShieldAlert : problem === 'error' ? AlertTriangle : PauseCircle;
  const key = problem ?? 'paused';
  return (
    <div
      role="status"
      className={`mb-4 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm ${tone}`}
      data-testid="email-intake-health"
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {t(`email-intake.queue.health.${key}Title`, { count: affected.length })}
        </p>
        <p className="truncate opacity-90">
          {t(`email-intake.queue.health.${key}Body`, {
            mailboxes: affected.map((m) => m.emailAddress).join(', '),
          })}
        </p>
      </div>
      {canConfigure ? (
        <Link
          to={SETTINGS_PATH}
          className="shrink-0 rounded border border-current px-3 py-1 text-xs font-medium hover:bg-white/60"
          data-testid="email-intake-health-action"
        >
          {t(`email-intake.queue.health.${key}Action`)}
        </Link>
      ) : (
        <span className="shrink-0 text-xs opacity-80">{t('email-intake.queue.health.askAdmin')}</span>
      )}
    </div>
  );
}

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'brand' | 'secondary';

/** Whether a translation exists (rather than comparing t()'s fallback with the key). */
export function hasTranslation(key: string): boolean {
  return i18next.exists(key);
}

/**
 * A failed provider sign-in, in words: our message, the provider's code
 * (e.g. Microsoft AADSTS50194) and, for the codes people actually hit, what
 * to change. Only codes travel in the URL — never the provider's free text.
 */
export function oauthErrorText(
  t: TFunction,
  code: string,
  providerCode?: string | null,
  providerError?: string | null
): string {
  const base = t(`email-intake.errors.${code}`, {
    defaultValue: t('email-intake.errors.OAUTH_FAILED'),
  });
  const safeCode = providerCode && /^AADSTS\d{4,9}$/.test(providerCode) ? providerCode : null;
  const safeError =
    providerError && /^[A-Za-z0-9_.-]{1,64}$/.test(providerError) ? providerError : null;
  const shown = safeCode ?? safeError;
  if (!shown) return base;
  const hintKey = `email-intake.oauthProvider.hints.${safeCode ?? safeError}`;
  const hint = hasTranslation(hintKey) ? t(hintKey) : '';
  return [t('email-intake.oauthProvider.withCode', { message: base, code: shown }), hint]
    .filter(Boolean)
    .join(' ');
}

/** Translate an API error by its stable code; fall back to the server message, then generic. */
export function intakeErrorText(t: TFunction, err: unknown): string {
  const code = apiErrorCode(err);
  if (code && hasTranslation(`email-intake.errors.${code}`)) {
    return t(`email-intake.errors.${code}`);
  }
  return apiErrorMessage(err) ?? t('email-intake.errors.generic');
}

/** One display status per email row, derived from the email + its proposals. */
export function emailDisplayStatus(
  e: Pick<InboundEmailSummary, 'status' | 'decisions' | 'classification' | 'mailboxMode'>
): string {
  if (e.decisions.some((d) => d === 'auto_created' || d === 'approved_created')) return 'created';
  if (e.decisions.some((d) => d === 'needs_review' || d === 'committing')) return 'needs_review';
  if (e.decisions.some((d) => d === 'shadow')) return 'shadow';
  if (
    e.status === 'proposed' &&
    e.decisions.length > 0 &&
    e.decisions.every((d) => d === 'rejected')
  )
    return 'rejected';
  if (
    e.status === 'ignored' &&
    (e.classification === 'load_update' || e.classification === 'load_cancellation')
  )
    return 'update';
  return e.status;
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  created: 'success',
  needs_review: 'warning',
  update: 'warning',
  shadow: 'secondary',
  failed: 'error',
  processing: 'info',
  received: 'info',
  ignored: 'neutral',
  filtered: 'neutral',
  rejected: 'neutral',
  proposed: 'info',
};

/** What an email's proposals become: 'load' (default), 'tender' or 'quote'. */
export type OutputNoun = 'load' | 'tender' | 'quote';

/**
 * i18next `context` for labels that name the record ("Quote created",
 * "Proposed quotes"); undefined = the load wording.
 */
export function nounContext(noun: OutputNoun | null | undefined): string | undefined {
  return noun === 'tender' || noun === 'quote' ? noun : undefined;
}

export function EmailStatusBadge({
  status,
  t,
  noun,
}: {
  status: string;
  t: TFunction;
  noun?: OutputNoun | null;
}) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'neutral'}>
      {t(`email-intake.status.${status}`, { defaultValue: status, context: nounContext(noun) })}
    </Badge>
  );
}

const MAILBOX_STATUS_VARIANT: Record<string, BadgeVariant> = {
  connected: 'success',
  paused: 'neutral',
  needs_reauth: 'error',
  error: 'warning',
  pending_approval: 'info',
};

export function MailboxStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <Badge variant={MAILBOX_STATUS_VARIANT[status] ?? 'neutral'}>
      {t(`email-intake.mailbox.statusLabel.${status}`, { defaultValue: status })}
    </Badge>
  );
}

const DECISION_VARIANT: Record<string, BadgeVariant> = {
  auto_created: 'success',
  approved_created: 'success',
  needs_review: 'warning',
  committing: 'info',
  shadow: 'secondary',
  rejected: 'neutral',
  failed: 'error',
};

export function DecisionBadge({ decision, t }: { decision: string; t: TFunction }) {
  return (
    <Badge variant={DECISION_VARIANT[decision] ?? 'neutral'}>
      {t(`email-intake.decision.${decision}`, { defaultValue: decision })}
    </Badge>
  );
}

/** A number with a unit (MB, s, min…) in the reader's locale. */
function formatUnit(lng: string, value: number, unit: string, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat(lng, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits,
  }).format(value);
}

export function formatBytes(n: number, lng = 'en'): string {
  if (n < 1024) return formatUnit(lng, n, 'byte');
  if (n < 1024 * 1024) return formatUnit(lng, Math.round(n / 1024), 'kilobyte');
  return formatUnit(lng, n / (1024 * 1024), 'megabyte', 1);
}

/** A duration: "350 ms", "4.2 s", "52 s", "3 min" (localized). */
export function formatDurationMs(ms: number | null | undefined, lng = 'en'): string {
  if (ms == null) return '—';
  if (ms < 1000) return formatUnit(lng, Math.round(ms), 'millisecond');
  if (ms < 10_000) return formatUnit(lng, ms / 1000, 'second', 1);
  if (ms < 90_000) return formatUnit(lng, Math.round(ms / 1000), 'second');
  return formatUnit(lng, Math.round(ms / 60_000), 'minute');
}

/** AI spend is always estimated in US dollars; the number format follows the reader. */
export function formatUsd(n: number, lng = 'en', fractionDigits = 2): string {
  return new Intl.NumberFormat(lng, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}
